import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const DOCUMENT_TYPE = "additional-evidence";
const DOCUMENT_KEY_CANDIDATES = [
  "additionalEvidence",
  "additional-evidence",
  "additional_evidence",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const pickExistingDocument = (applicationDocuments) => {
  if (!applicationDocuments) return null;
  for (const key of DOCUMENT_KEY_CANDIDATES) {
    if (applicationDocuments[key]?.fileKey) {
      return applicationDocuments[key];
    }
  }
  return null;
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

const readCredentialsFromStorage = () => {
  try {
    const sessionData = sessionStorage.getItem("applicationData");
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      const phoneNumber = parsed.phoneNumber || parsed.client?.phoneNumber;
      const accessCode = parsed.accessCode;
      if (phoneNumber && accessCode) return { phoneNumber, accessCode };
    }
  } catch (e) {}

  try {
    const cookieData = Cookies.get("additionalEvidenceApplication");
    if (cookieData) {
      const parsed = JSON.parse(cookieData);
      if (parsed.phoneNumber && parsed.accessCode) {
        return {
          phoneNumber: parsed.phoneNumber,
          accessCode: parsed.accessCode,
        };
      }
    }
  } catch (e) {}

  return null;
};

const StatusAdditionalEvidencePage = () => {
  const navigate = useNavigate();
  const { applicationNumber } = useParams();
  const fileInputRef = useRef(null);

  const [applicationData, setApplicationData] = useState(null);
  const [existingDocument, setExistingDocument] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        if (!applicationNumber) {
          setLoadError("신청서 번호가 없습니다.");
          return;
        }

        const credentials = readCredentialsFromStorage();
        if (!credentials) {
          setLoadError(
            "신청서 정보를 찾을 수 없습니다. 처음부터 다시 시도해주세요.",
          );
          return;
        }

        const verifyResponse = await fetch(
          `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...credentials, lawsuitId: 3 }),
          },
        );

        if (!verifyResponse.ok) {
          setLoadError("신청서 정보를 불러오지 못했습니다.");
          return;
        }

        const latest = await verifyResponse.json();
        setApplicationData(latest);

        const existing = pickExistingDocument(latest.applicationDocuments);
        if (existing?.fileKey) {
          setExistingDocument(existing);
          try {
            const signedResponse = await fetch(
              `${API_ENDPOINTS.BASE_URL}/uploads/common-signed-url/${encodeURIComponent(
                existing.fileKey,
              )}`,
            );
            if (signedResponse.ok) {
              const signedData = await signedResponse.json();
              if (signedData.signedUrl) {
                setExistingFileUrl(signedData.signedUrl);
              }
            }
          } catch (err) {
            console.error("기존 증거 자료 URL 가져오기 실패:", err);
          }
        }
      } catch (err) {
        console.error("신청서 조회 실패:", err);
        setLoadError("신청서 정보를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [applicationNumber]);

  useEffect(() => {
    if (showZoom) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showZoom]);

  const handlePickFile = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    setErrorMessage("");
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;

    if (!selected.type.startsWith("image/")) {
      setErrorMessage("이미지 파일만 업로드할 수 있습니다.");
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      setErrorMessage("이미지 용량은 10MB 이하만 가능합니다.");
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const handleClearFile = () => {
    if (isUploading) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
    setErrorMessage("");
  };

  const handleSubmit = async () => {
    if (!file || isUploading || !applicationNumber) return;
    setErrorMessage("");
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file, file.name);

      const updateUrl = `${API_ENDPOINTS.BASE_URL}/applications/${applicationNumber}/documents/${DOCUMENT_TYPE}`;
      const response = await fetch(updateUrl, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok && response.status !== 201) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || "수정에 실패했습니다.");
      }

      // 최신 데이터 갱신 (sessionStorage)
      try {
        const credentials = readCredentialsFromStorage();
        if (credentials) {
          const verifyResponse = await fetch(
            `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ...credentials, lawsuitId: 3 }),
            },
          );
          if (verifyResponse.ok) {
            const latest = await verifyResponse.json();
            sessionStorage.setItem("applicationData", JSON.stringify(latest));
          }
        }
      } catch (err) {
        // 갱신 실패는 무시
      }

      navigate("/additionalevidence/complete", { replace: true });
    } catch (error) {
      setErrorMessage(error.message || "수정 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  const userName =
    applicationData?.name || applicationData?.client?.name || "-";
  const accessCodeDisplay = applicationData?.accessCode || "-";
  const createdAtDisplay = formatDate(applicationData?.createdAt);

  if (isLoading) {
    return (
      <PageBackground>
        <Container>
          <Content>
            <LoadingBox>신청서 정보를 불러오는 중...</LoadingBox>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  if (loadError) {
    return (
      <PageBackground>
        <Container>
          <Content>
            <ErrorBox>{loadError}</ErrorBox>
            <ButtonsContainer>
              <ActionButton onClick={() => navigate("/")}>
                홈으로 돌아가기
              </ActionButton>
            </ButtonsContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container>
        <Content>
          <Header>
            <SubTitle>증거자료 수정</SubTitle>
            <Description>
              이전에 제출하신 증거자료를 확인하고 새 이미지로 수정할 수
              있습니다.
            </Description>
          </Header>

          <UserInfoCard>
            <UserInfoRow>
              <UserInfoLabel>이름</UserInfoLabel>
              <UserInfoValue>{userName}</UserInfoValue>
            </UserInfoRow>
            <UserInfoDivider />
            <UserInfoRow>
              <UserInfoLabel>신청서 번호</UserInfoLabel>
              <UserInfoValue>{accessCodeDisplay}</UserInfoValue>
            </UserInfoRow>
            <UserInfoDivider />
            <UserInfoRow>
              <UserInfoLabel>접수일자</UserInfoLabel>
              <UserInfoValue>{createdAtDisplay}</UserInfoValue>
            </UserInfoRow>
          </UserInfoCard>

          <SectionHeader>
            <SectionTitle>제출된 증거 자료</SectionTitle>
            <SectionDescription>
              새 이미지를 선택하면 기존 자료가 교체됩니다.
            </SectionDescription>
          </SectionHeader>

          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />

          {!previewUrl && existingFileUrl && (
            <PreviewWrapper>
              <PreviewImageButton
                type="button"
                onClick={() => setShowZoom(true)}
                aria-label="이미지 크게 보기"
              >
                <PreviewImage src={existingFileUrl} alt="이전 증거 자료" />
                <ExistingBadge>이전 제출 자료</ExistingBadge>
                <PreviewZoomHint>눌러서 크게 보기</PreviewZoomHint>
              </PreviewImageButton>
              <ReplaceRow>
                <ReplaceButton onClick={handlePickFile}>
                  이미지 변경
                </ReplaceButton>
              </ReplaceRow>
            </PreviewWrapper>
          )}

          {!previewUrl && !existingFileUrl && (
            <UploadBox
              onClick={handlePickFile}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handlePickFile();
              }}
            >
              <UploadIcon>＋</UploadIcon>
              <UploadBoxTitle>이미지 추가</UploadBoxTitle>
              <UploadBoxHint>JPG, PNG · 10MB 이하 · 1장</UploadBoxHint>
            </UploadBox>
          )}

          {previewUrl && (
            <PreviewWrapper>
              <PreviewImage src={previewUrl} alt="증거 자료 미리보기" />
              <RemoveButton onClick={handleClearFile}>×</RemoveButton>
              <FileNameRow>
                <FileName>{file?.name}</FileName>
                <FileSize>{(file?.size / 1024 / 1024).toFixed(2)}MB</FileSize>
              </FileNameRow>
              <ReplaceHint>
                제출 시 기존 자료가 새 이미지로 교체됩니다.
              </ReplaceHint>
            </PreviewWrapper>
          )}

          {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}

          <ButtonsContainer>
            <ActionButton
              onClick={handleSubmit}
              whileHover={{ scale: file && !isUploading ? 1.02 : 1 }}
              whileTap={{ scale: file && !isUploading ? 0.98 : 1 }}
              disabled={!file || isUploading}
            >
              {isUploading ? "수정 중..." : "수정 제출하기"}
            </ActionButton>
          </ButtonsContainer>
        </Content>
      </Container>

      <AnimatePresence>
        {showZoom && existingFileUrl && (
          <ZoomOverlay
            onClick={() => setShowZoom(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ZoomScroll onClick={(e) => e.stopPropagation()}>
              <ZoomImage src={existingFileUrl} alt="이전 증거 자료 (확대)" />
            </ZoomScroll>
            <ZoomClose onClick={() => setShowZoom(false)}>×</ZoomClose>
          </ZoomOverlay>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    padding: 0 0 80px 0;
    max-width: 100%;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px;
  }
`;

const LoadingBox = styled.div`
  width: 100%;
  padding: 80px 24px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 8px 0;
  word-break: keep-all;
  color: #111827;
`;

const Description = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  word-break: keep-all;
  color: #6b7280;
`;

const UserInfoCard = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 4px 20px;
  margin-bottom: 24px;
  box-sizing: border-box;
`;

const UserInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  gap: 12px;
`;

const UserInfoDivider = styled.div`
  height: 1px;
  background-color: #f0f0f0;
`;

const UserInfoLabel = styled.span`
  font-size: 14px;
  color: #999;
  font-weight: 500;
  flex-shrink: 0;
`;

const UserInfoValue = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 600;
  text-align: right;
  word-break: break-all;
`;

const SectionHeader = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 6px 0;
`;

const SectionDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadBox = styled.div`
  width: 100%;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  padding: 48px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  background-color: #fafafa;
  margin-bottom: 16px;

  &:hover {
    border-color: #e31c20;
    background-color: #fff7f7;
  }

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const UploadIcon = styled.div`
  font-size: 36px;
  color: #999;
  line-height: 1;
  margin-bottom: 4px;
`;

const UploadBoxTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const UploadBoxHint = styled.div`
  font-size: 13px;
  color: #999;
`;

const PreviewWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const PreviewImageButton = styled.button`
  width: 100%;
  position: relative;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  display: block;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  background-color: #fafafa;
  display: block;
`;

const PreviewZoomHint = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
`;

const ExistingBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
`;

const ReplaceRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const ReplaceButton = styled.button`
  background-color: #f7f7f7;
  color: #e31c20;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 18px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const FileNameRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const FileName = styled.span`
  font-size: 13px;
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #999;
  flex-shrink: 0;
`;

const ReplaceHint = styled.div`
  font-size: 12px;
  color: #fe7156;
  font-weight: 600;
`;

const ErrorBox = styled.div`
  width: 100%;
  padding: 12px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 16px;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
`;

const ZoomOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 0 40px;
`;

const ZoomScroll = styled.div`
  width: 92%;
  max-width: 600px;
  display: flex;
  justify-content: center;
`;

const ZoomImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  background-color: white;
`;

const ZoomClose = styled.button`
  position: fixed;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 24px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 10000;
`;

export default StatusAdditionalEvidencePage;
